import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { fetchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { useI18n } from '../i18n';
import { isIsraelCountryId } from '../utils/assetCreateFormDefaults';
import {
  cardPaymentIsValid,
  validateCardPayment,
  type CardPaymentFieldErrors,
} from '../utils/bookingPaymentValidate';
import { resolveProfileCountryIdOrIsrael } from '../utils/profileAssetOwnerEdit';
import {
  formatCardExpiryInput,
  formatCardNumberInput,
  formatCvcInput,
  formatNationalIdInput,
  formatProfilePaymentCardSummaryLine,
  getStoredProfilePaymentCards,
  paymentCardSummaryFromValidatedInput,
  persistProfilePaymentCards,
  PROFILE_PAYMENT_CARDS_MAX,
  setProfilePaymentCardPrimary,
  type ProfileSavedPaymentCard,
} from '../utils/profilePaymentCard';
import { PaymentCardBrandIcon } from './PaymentCardBrandIcon';
import pageThemeStyles from './menuPageTheme.module.css';
import styles from './ProfileMenu.module.css';

export type ProfilePaymentMethodsEditPanelProps = {
  onBack: () => void;
  onSaved?: (cards: ProfileSavedPaymentCard[]) => void;
};

export function ProfilePaymentMethodsEditPanel({
  onBack,
  onSaved,
}: ProfilePaymentMethodsEditPanelProps) {
  const { t, language } = useI18n();
  const panelId = useId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Existing users without country_id default to Israel (national ID required). */
  const [requireNationalId, setRequireNationalId] = useState(true);
  const [cards, setCards] = useState<ProfileSavedPaymentCard[]>([]);
  const [adding, setAdding] = useState(false);
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [fieldErrors, setFieldErrors] = useState<CardPaymentFieldErrors>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const stored = getStoredProfilePaymentCards(getStoredLoginIdentifier());
      if (!cancelled) {
        setCards(stored);
        setAdding(stored.length === 0);
      }
      try {
        const data = await fetchProfile();
        if (!cancelled) {
          setRequireNationalId(isIsraelCountryId(resolveProfileCountryIdOrIsrael(data)));
        }
      } catch {
        if (!cancelled) {
          setRequireNationalId(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cardMessages = useMemo(
    () => ({
      errorNationalId: t('bookings.payment.errorNationalId'),
      errorName: t('bookings.payment.errorName'),
      errorNameInvalid: t('bookings.payment.errorNameInvalid'),
      errorNumber: t('bookings.payment.errorNumber'),
      errorNumberLuhn: t('bookings.payment.errorNumberLuhn'),
      errorExpiry: t('bookings.payment.errorExpiry'),
      errorExpiryPast: t('bookings.payment.errorExpiryPast'),
      errorCvc: t('bookings.payment.errorCvc'),
    }),
    [t],
  );

  const clearFieldError = useCallback((key: keyof CardPaymentFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setError(null);
  }, []);

  const resetAddForm = useCallback(() => {
    setCardName('');
    setCardNumber('');
    setExpiry('');
    setCvc('');
    setNationalId('');
    setFieldErrors({});
    setError(null);
  }, []);

  const persistAndNotify = useCallback(
    (next: ProfileSavedPaymentCard[]) => {
      persistProfilePaymentCards(next, getStoredLoginIdentifier());
      setCards(next);
      onSaved?.(next);
    },
    [onSaved],
  );

  const onRemoveCard = useCallback(
    (cardId: string) => {
      const next = cards.filter((card) => card.id !== cardId);
      const normalized =
        next.length === 0
          ? []
          : next.some((card) => card.isPrimary)
            ? next
            : next.map((card, index) => ({ ...card, isPrimary: index === 0 }));
      persistAndNotify(normalized);
      if (normalized.length === 0) {
        setAdding(true);
      }
    },
    [cards, persistAndNotify],
  );

  const onSetPrimary = useCallback(
    (cardId: string) => {
      persistAndNotify(setProfilePaymentCardPrimary(cards, cardId));
    },
    [cards, persistAndNotify],
  );

  const onStartAdd = useCallback(() => {
    if (cards.length >= PROFILE_PAYMENT_CARDS_MAX) {
      setError(t('profile.paymentMethods.errorMax'));
      return;
    }
    resetAddForm();
    setAdding(true);
  }, [cards.length, resetAddForm, t]);

  const onCancelAdd = useCallback(() => {
    resetAddForm();
    setAdding(false);
  }, [resetAddForm]);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (cards.length >= PROFILE_PAYMENT_CARDS_MAX) {
        setError(t('profile.paymentMethods.errorMax'));
        return;
      }
      const errors = validateCardPayment(
        {
          cardName,
          cardNumber,
          expiry,
          cvc,
          nationalId,
          requireNationalId,
        },
        cardMessages,
      );
      setFieldErrors(errors);
      if (!cardPaymentIsValid(errors)) {
        setError(
          errors.cardNumber ??
            errors.expiry ??
            errors.cvc ??
            errors.cardName ??
            errors.nationalId ??
            t('common.somethingWrong'),
        );
        return;
      }
      const last4 = cardNumber.replace(/\D+/g, '').slice(-4);
      if (cards.some((card) => card.last4 === last4 && card.expiry === formatCardExpiryInput(expiry))) {
        setError(t('profile.paymentMethods.errorDuplicate'));
        return;
      }
      setSaving(true);
      setError(null);
      try {
        const summary = paymentCardSummaryFromValidatedInput({
          cardNumber,
          expiry,
          cardholderName: cardName,
          nationalId,
          isPrimary: cards.length === 0,
        });
        const next = [...cards, summary].slice(0, PROFILE_PAYMENT_CARDS_MAX);
        persistAndNotify(next);
        resetAddForm();
        setAdding(false);
      } catch {
        setError(t('common.somethingWrong'));
      } finally {
        setSaving(false);
      }
    },
    [
      cardMessages,
      cardName,
      cardNumber,
      cards,
      cvc,
      expiry,
      nationalId,
      persistAndNotify,
      requireNationalId,
      resetAddForm,
      t,
    ],
  );

  const textDir = language === 'he' ? 'rtl' : 'ltr';
  const fieldError = (key: keyof CardPaymentFieldErrors) => fieldErrors[key];
  const atMax = cards.length >= PROFILE_PAYMENT_CARDS_MAX;

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('profile.hub.editPaymentMethods')}
    >
      <div className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}>
        <div className={styles.hubSubPanel} dir={textDir}>
          <div className={styles.hubTopBar}>
            <button
              type="button"
              className={styles.hubTopBarBtn}
              onClick={onBack}
              aria-label={t('profile.hub.back')}
            >
              <ProfileBackIcon rtl={language === 'he'} />
            </button>
            <h2 className={styles.hubTopBarTitle}>{t('profile.hub.editPaymentMethods')}</h2>
            <span className={styles.hubTopBarSpacer} aria-hidden />
          </div>

          {loading ? (
            <p className={pageThemeStyles.browseEmpty} role="status">
              {t('common.loading')}
            </p>
          ) : (
            <div className={styles.profileEditForm} dir={textDir}>
              <p className={styles.profileEditPasswordOptional}>
                {t('profile.paymentMethods.cardIntro')}
              </p>

              {cards.length === 0 && !adding ? (
                <p className={styles.profileEditVehiclesEmpty}>{t('profile.paymentMethods.empty')}</p>
              ) : null}

              {cards.length > 0 ? (
                <ul className={styles.hubVehiclesList}>
                  {cards.map((card) => (
                    <li key={card.id} className={styles.hubVehicleItem}>
                      <PaymentCardBrandIcon brand={card.brand} />
                      <div className={styles.profileEditVehiclesHeaderText}>
                        <span className={styles.hubVehicleLine} dir="ltr">
                          {formatProfilePaymentCardSummaryLine(card)}
                        </span>
                        {card.isPrimary ? (
                          <span className={styles.hubVehiclePrimaryBadge}>
                            {t('profile.paymentMethods.primary')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={styles.hubDetailsEditBtn}
                            disabled={saving}
                            onClick={() => onSetPrimary(card.id)}
                          >
                            {t('profile.paymentMethods.setPrimary')}
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        className={styles.createImagesRemove}
                        disabled={saving}
                        onClick={() => onRemoveCard(card.id)}
                      >
                        {t('profile.paymentMethods.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              {!adding && !atMax ? (
                <button
                  type="button"
                  className={styles.hubDetailsEditBtn}
                  disabled={saving}
                  onClick={onStartAdd}
                >
                  {t('profile.paymentMethods.add')}
                </button>
              ) : null}
              {atMax && !adding ? (
                <p className={styles.profileEditVehiclesLimit} role="status">
                  {t('profile.paymentMethods.maxReached')}
                </p>
              ) : null}

              {adding ? (
                <form onSubmit={onSubmit}>
                  <div className={styles.profileEditVehiclesHeader}>
                    <h3 className={styles.profileEditPasswordTitle}>
                      {t('profile.paymentMethods.add')}
                    </h3>
                    {cards.length > 0 ? (
                      <button
                        type="button"
                        className={styles.profileEditVehiclesCancelBtn}
                        onClick={onCancelAdd}
                        disabled={saving}
                        aria-label={t('common.cancel')}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>

                  <p className={styles.paymentCardLocalDisclaimer} role="note">
                    {t('profile.paymentMethods.secureNote')}
                  </p>

                  <div className={styles.profileEditField}>
                    <label className={styles.profileEditLabel} htmlFor={`${panelId}-card-name`}>
                      {t('bookings.payment.cardName')}
                    </label>
                    <input
                      id={`${panelId}-card-name`}
                      className={styles.profileEditInput}
                      value={cardName}
                      disabled={saving}
                      autoComplete="cc-name"
                      placeholder={t('bookings.payment.cardNamePlaceholder')}
                      onChange={(event) => {
                        setCardName(event.target.value);
                        clearFieldError('cardName');
                      }}
                      aria-invalid={fieldError('cardName') ? true : undefined}
                    />
                  </div>
                  {fieldError('cardName') ? (
                    <p className={styles.profileEditError} role="alert">
                      {fieldError('cardName')}
                    </p>
                  ) : null}

                  <div className={styles.profileEditField}>
                    <label className={styles.profileEditLabel} htmlFor={`${panelId}-card-number`}>
                      {t('bookings.payment.cardNumber')}
                    </label>
                    <div className={styles.profileEditCardNumberRow}>
                      <input
                        id={`${panelId}-card-number`}
                        className={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
                        dir="ltr"
                        inputMode="numeric"
                        autoComplete="cc-number"
                        value={cardNumber}
                        disabled={saving}
                        placeholder="•••• •••• •••• ••••"
                        onChange={(event) => {
                          setCardNumber(formatCardNumberInput(event.target.value));
                          clearFieldError('cardNumber');
                        }}
                        aria-invalid={fieldError('cardNumber') ? true : undefined}
                      />
                      <PaymentCardBrandIcon cardNumber={cardNumber} />
                    </div>
                  </div>
                  {fieldError('cardNumber') ? (
                    <p className={styles.profileEditError} role="alert">
                      {fieldError('cardNumber')}
                    </p>
                  ) : null}

                  <div className={styles.profileEditField}>
                    <label className={styles.profileEditLabel} htmlFor={`${panelId}-expiry`}>
                      {t('bookings.payment.expiry')}
                    </label>
                    <input
                      id={`${panelId}-expiry`}
                      className={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
                      dir="ltr"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      value={expiry}
                      disabled={saving}
                      placeholder="MM/YY"
                      onChange={(event) => {
                        setExpiry(formatCardExpiryInput(event.target.value));
                        clearFieldError('expiry');
                      }}
                      aria-invalid={fieldError('expiry') ? true : undefined}
                    />
                  </div>
                  {fieldError('expiry') ? (
                    <p className={styles.profileEditError} role="alert">
                      {fieldError('expiry')}
                    </p>
                  ) : null}

                  <div className={styles.profileEditField}>
                    <label className={styles.profileEditLabel} htmlFor={`${panelId}-cvc`}>
                      {t('bookings.payment.cvc')}
                    </label>
                    <input
                      id={`${panelId}-cvc`}
                      className={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
                      dir="ltr"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      value={cvc}
                      disabled={saving}
                      placeholder="•••"
                      onChange={(event) => {
                        setCvc(formatCvcInput(event.target.value));
                        clearFieldError('cvc');
                      }}
                      aria-invalid={fieldError('cvc') ? true : undefined}
                    />
                  </div>
                  {fieldError('cvc') ? (
                    <p className={styles.profileEditError} role="alert">
                      {fieldError('cvc')}
                    </p>
                  ) : null}

                  {requireNationalId ? (
                    <>
                      <div className={styles.profileEditField}>
                        <label className={styles.profileEditLabel} htmlFor={`${panelId}-national-id`}>
                          {t('bookings.payment.nationalId')}
                        </label>
                        <input
                          id={`${panelId}-national-id`}
                          className={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
                          dir="ltr"
                          inputMode="numeric"
                          autoComplete="off"
                          value={nationalId}
                          disabled={saving}
                          placeholder={t('bookings.payment.nationalIdPlaceholder')}
                          onChange={(event) => {
                            setNationalId(formatNationalIdInput(event.target.value));
                            clearFieldError('nationalId');
                          }}
                          aria-invalid={fieldError('nationalId') ? true : undefined}
                        />
                      </div>
                      {fieldError('nationalId') ? (
                        <p className={styles.profileEditError} role="alert">
                          {fieldError('nationalId')}
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {error ? (
                    <p className={styles.profileEditError} role="alert">
                      {error}
                    </p>
                  ) : null}

                  <button type="submit" className={styles.profileEditSaveBtn} disabled={saving}>
                    {saving ? t('common.submitting') : t('profile.paymentMethods.saveCard')}
                  </button>
                </form>
              ) : error ? (
                <p className={styles.profileEditError} role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileBackIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path
        d={rtl ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
